import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridValidRowModel,
  NoRowsOverlayPropsOverrides,
} from '@mui/x-data-grid/models';
import { DataGridProps } from '@mui/x-data-grid/internals';
import { ListResponse } from '@/common/api/schemas.ts';
import { GridState } from '@/common/api/sieveAdapter.ts';
import { DataGrid } from '@mui/x-data-grid/DataGrid';
import { CONFIG } from '@/config';
import { GridConfig } from '@/common/components/app-data-grid/GridConfig.ts';
import { ReactNode } from 'react';
import { ErrorDisplay } from '@/common/components/error-display/ErrorDisplay.tsx';
import { Trans } from '@lingui/react/macro';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';

export interface AppDataGridProps<TModel extends GridValidRowModel>
  extends Omit<
    DataGridProps<TModel>,
    | 'rows'
    | 'rowCount'
    | 'sortingMode'
    | 'filterMode'
    | 'paginationMode'
    | 'sortModel'
    | 'onSortModelChange'
    | 'onFilterModelChange'
    | 'onPaginationModelChange'
    | 'filterModel'
    | 'paginationModel'
    | 'pageSizeOptions'
  > {
  list?: ListResponse<TModel>;
  gridState: GridState;
  onGridStateChange: (gridState: GridState) => void;
  config: GridConfig<TModel>;
  onRefetch: () => void;
  dataFetchError?: ReactNode | true;
}

export const AppDataGrid = <TModel extends GridValidRowModel>({
  list,
  gridState,
  onGridStateChange,
  config,
  dataFetchError,
  onRefetch,
  ...gridProps
}: AppDataGridProps<TModel>) => {
  const handleSortModelChange = (sortModel: GridSortModel) => {
    onGridStateChange({
      ...gridState,
      sorts: sortModel,
    });
  };

  const handleFilterModelChange = (filterModel: GridFilterModel) => {
    onGridStateChange({
      ...gridState,
      filters: filterModel,
    });
  };

  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    onGridStateChange({
      ...gridState,
      pagination: paginationModel,
    });
  };

  return (
    <DataGrid
      {...gridProps}
      rows={list?.result}
      rowCount={list?.rowCount ?? -1}
      density="compact"
      disableColumnSelector={gridProps.disableColumnSelector ?? true}
      initialState={{
        filter: { filterModel: gridState.filters ?? { items: [] } },
      }}
      sortingMode="server"
      sortModel={gridState.sorts ?? []}
      onSortModelChange={handleSortModelChange}
      filterMode="server"
      onFilterModelChange={handleFilterModelChange}
      paginationMode="server"
      pageSizeOptions={config.pageSizeOptions ?? CONFIG.gridPageSizes}
      paginationModel={gridState.pagination}
      onPaginationModelChange={handlePaginationModelChange}
      slots={{
        noRowsOverlay: dataFetchError ? ErrorDisplay : undefined,
      }}
      slotProps={{
        noRowsOverlay: dataFetchError
          ? ({
              message:
                dataFetchError === true ? (
                  <Trans>Could not load data due to an error.</Trans>
                ) : (
                  dataFetchError
                ),
              actions: (
                <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRefetch}>
                  <Trans>Try again</Trans>
                </Button>
              ),
            } as NoRowsOverlayPropsOverrides)
          : undefined,
      }}
    />
  );
};
